import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  Music as MusicIcon,
  Play,
  ShoppingCart,
  Check,
  Loader2,
  ExternalLink,
  Gift,
  Disc3,
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useMusic } from '../context/MusicContext';
import { db } from '../services/firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';

export default function Music() {
  const { addToCart } = useCart();
  const { playTrack } = useMusic();
  const [releases, setReleases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [addedToCart, setAddedToCart] = useState({});

  // Fetch music from Firestore
  useEffect(() => {
    if (!db) {
      setIsLoading(false);
      return;
    }

    const q = query(collection(db, 'music'), orderBy('releaseDate', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const musicData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setReleases(musicData);
      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  const handleAddToCart = (release) => {
    const product = {
      id: release.id,
      name: release.title,
      price: parseFloat(release.price) || 0.99,
      image: release.coverImage,
      type: 'music',
    };

    addToCart(product);

    setAddedToCart((prev) => ({ ...prev, [release.id]: true }));
    setTimeout(() => {
      setAddedToCart((prev) => ({ ...prev, [release.id]: false }));
    }, 2000);
  };

  const handlePlayFree = (release) => {
    if (release.audioUrl) {
      playTrack(release);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '$0.99';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  // Separate releases by type
  const singles = releases.filter((r) => r.type === 'single');
  const albums = releases.filter((r) => r.type === 'album' || r.type === 'ep' || r.type === 'mixtape');
  const freeReleases = releases.filter((r) => r.isFree);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero */}
      <motion.section
        className="py-32 px-6 bg-gradient-to-b from-[#0a0a0a] to-black"
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        <div className="container mx-auto text-center">
          <MusicIcon className="w-16 h-16 text-orange-500 mx-auto mb-6" />
          <h1 className="text-6xl md:text-7xl font-bold mb-6">
            <span className="text-orange-500">Music</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Stream or purchase singles and albums - Support independent music
          </p>
        </div>
      </motion.section>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
        </div>
      ) : (
        <>
          {/* Free Tracks Section */}
          {freeReleases.length > 0 && (
            <section className="py-16 px-6 bg-gradient-to-b from-green-900/20 to-black">
              <div className="container mx-auto">
                <div className="flex items-center justify-center gap-3 mb-8">
                  <Gift className="w-8 h-8 text-green-500" />
                  <h2 className="text-3xl font-bold text-center">Free Tracks</h2>
                </div>
                <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
                  Listen to these tracks for free! Click play to spin the vinyl.
                </p>
                <motion.div
                  className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={{
                    hidden: { opacity: 0 },
                    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
                  }}
                >
                  {freeReleases.map((release) => (
                    <motion.div
                      key={release.id}
                      className="group bg-gradient-to-b from-green-900/30 to-[#0f0f0f] rounded-xl overflow-hidden border border-green-500/30 hover:border-green-500 transition-all cursor-pointer"
                      variants={fadeInUp}
                      onClick={() => handlePlayFree(release)}
                    >
                      <div className="relative aspect-square bg-gray-800 overflow-hidden">
                        {release.coverImage ? (
                          <img
                            src={release.coverImage}
                            alt={release.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Disc3 className="w-20 h-20 text-gray-600" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center">
                            <Play className="w-8 h-8 text-white ml-1" fill="white" />
                          </div>
                        </div>
                        <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                          <Gift className="w-3 h-3" />
                          FREE
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold truncate">{release.title}</h3>
                        <p className="text-gray-400 text-sm">Click to play</p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </section>
          )}

          {/* Albums Section */}
          {albums.length > 0 && (
            <section className="py-20 px-6 bg-black">
              <div className="container mx-auto">
                <h2 className="text-4xl font-bold mb-12 text-center">Albums & EPs</h2>
                <motion.div
                  className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={{
                    hidden: { opacity: 0 },
                    visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
                  }}
                >
                  {albums.map((album) => (
                    <motion.div
                      key={album.id}
                      className="bg-gradient-to-b from-[#1a1a1a] to-[#0f0f0f] rounded-xl overflow-hidden border border-gray-800 hover:border-orange-500/50 transition-all"
                      variants={fadeInUp}
                    >
                      <div className="md:flex">
                        {/* Album Cover */}
                        <div className="md:w-64 flex-shrink-0">
                          <div className="relative aspect-square bg-gray-800">
                            {album.coverImage ? (
                              <img
                                src={album.coverImage}
                                alt={album.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Disc3 className="w-20 h-20 text-gray-600" />
                              </div>
                            )}
                            <span className="absolute top-2 right-2 px-2 py-1 bg-orange-500 text-white text-xs font-semibold rounded capitalize">
                              {album.type}
                            </span>
                          </div>
                        </div>

                        {/* Album Info */}
                        <div className="p-6 flex-1 flex flex-col">
                          <div className="flex-1">
                            <h3 className="text-2xl font-bold mb-2">{album.title}</h3>
                            <p className="text-gray-400 text-sm mb-4">
                              {album.description || 'Full project available now'}
                            </p>
                            {album.releaseDate && (
                              <p className="text-gray-500 text-sm mb-4">
                                Released: {(() => { const [y,m,d] = String(album.releaseDate).split('-').map(Number); return new Date(y, m-1, d).toLocaleDateString('en-US', { timeZone: 'America/New_York' }); })()}
                              </p>
                            )}

                            {/* Streaming Links */}
                            <div className="flex flex-wrap gap-2 mb-4">
                              {album.spotifyUrl && (
                                <a
                                  href={album.spotifyUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-green-500 hover:text-green-400 text-sm"
                                >
                                  <ExternalLink className="w-3 h-3" /> Spotify
                                </a>
                              )}
                              {album.appleMusicUrl && (
                                <a
                                  href={album.appleMusicUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-pink-500 hover:text-pink-400 text-sm"
                                >
                                  <ExternalLink className="w-3 h-3" /> Apple Music
                                </a>
                              )}
                            </div>
                          </div>

                          {/* Purchase */}
                          {!album.isFree && album.albumPrice && (
                            <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                              <span className="text-2xl font-bold text-orange-500">
                                {formatPrice(album.albumPrice)}
                              </span>
                              <Button
                                onClick={() => handleAddToCart({ ...album, price: album.albumPrice })}
                                className={`${
                                  addedToCart[album.id]
                                    ? 'bg-green-600 hover:bg-green-700'
                                    : 'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700'
                                } text-white font-semibold`}
                              >
                                {addedToCart[album.id] ? (
                                  <>
                                    <Check className="w-4 h-4 mr-2" />
                                    Added
                                  </>
                                ) : (
                                  <>
                                    <ShoppingCart className="w-4 h-4 mr-2" />
                                    Buy Album
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </section>
          )}

          {/* Singles Section */}
          {singles.length > 0 && (
            <section className="py-20 px-6 bg-[#0a0a0a]">
              <div className="container mx-auto">
                <h2 className="text-4xl font-bold mb-12 text-center">Singles</h2>
                <motion.div
                  className="grid md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-7xl mx-auto"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={{
                    hidden: { opacity: 0 },
                    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
                  }}
                >
                  {singles.map((single) => (
                    <motion.div
                      key={single.id}
                      className="group bg-gradient-to-b from-[#1a1a1a] to-[#0f0f0f] rounded-xl overflow-hidden border border-gray-800 hover:border-orange-500/50 transition-all"
                      variants={fadeInUp}
                    >
                      {/* Cover Art */}
                      <div className="relative aspect-square bg-gray-800 overflow-hidden">
                        {single.coverImage ? (
                          <img
                            src={single.coverImage}
                            alt={single.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Disc3 className="w-16 h-16 text-gray-600" />
                          </div>
                        )}

                        {/* Play overlay for free tracks */}
                        {single.isFree ? (
                          <div
                            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                            onClick={() => handlePlayFree(single)}
                          >
                            <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                              <Play className="w-6 h-6 text-white ml-0.5" fill="white" />
                            </div>
                          </div>
                        ) : (
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}

                        {/* Badges */}
                        {single.isFree ? (
                          <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                            <Gift className="w-3 h-3" />
                            FREE
                          </div>
                        ) : null}
                      </div>

                      {/* Single Info */}
                      <div className="p-4">
                        <h3 className="font-bold mb-1 truncate">{single.title}</h3>
                        <p className="text-gray-400 text-xs mb-3">
                          {single.releaseDate
                            ? (() => { const [y,m,d] = String(single.releaseDate).split('-').map(Number); return new Date(y, m-1, d).toLocaleDateString('en-US', { timeZone: 'America/New_York' }); })()
                            : 'Single'}
                        </p>

                        {/* Streaming or Purchase */}
                        {single.isFree ? (
                          <Button
                            onClick={() => handlePlayFree(single)}
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold"
                            size="sm"
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Play Free
                          </Button>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span className="text-lg font-bold text-orange-500">
                              {formatPrice(single.price)}
                            </span>
                            <Button
                              onClick={() => handleAddToCart(single)}
                              className={`${
                                addedToCart[single.id]
                                  ? 'bg-green-600 hover:bg-green-700'
                                  : 'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700'
                              } text-white font-semibold`}
                              size="sm"
                            >
                              {addedToCart[single.id] ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <ShoppingCart className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        )}

                        {/* Streaming links */}
                        {(single.spotifyUrl || single.youtubeUrl) && (
                          <div className="flex gap-3 mt-3 pt-3 border-t border-gray-800">
                            {single.spotifyUrl && (
                              <a
                                href={single.spotifyUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-500 hover:text-green-400 text-xs flex items-center gap-1"
                              >
                                <ExternalLink className="w-3 h-3" /> Spotify
                              </a>
                            )}
                            {single.youtubeUrl && (
                              <a
                                href={single.youtubeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-red-500 hover:text-red-400 text-xs flex items-center gap-1"
                              >
                                <ExternalLink className="w-3 h-3" /> YouTube
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </section>
          )}

          {/* Empty State */}
          {releases.length === 0 && (
            <section className="py-20 px-6">
              <div className="text-center">
                <Disc3 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Music Yet</h3>
                <p className="text-gray-400">Check back soon for new releases!</p>
              </div>
            </section>
          )}

          {/* Streaming Platforms */}
          <motion.section
            className="py-20 px-6 bg-black"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="container mx-auto text-center">
              <h2 className="text-4xl font-bold mb-4">Stream Everywhere</h2>
              <p className="text-gray-400 mb-12 max-w-2xl mx-auto">
                Listen to Scooby DaGodSon & Sirius Company on your favorite streaming platform
              </p>
              <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
                {[
                  { name: 'Spotify', url: 'https://open.spotify.com' },
                  { name: 'Apple Music', url: 'https://music.apple.com' },
                  { name: 'YouTube Music', url: 'https://music.youtube.com' },
                  { name: 'SoundCloud', url: 'https://soundcloud.com' },
                ].map((platform) => (
                  <a
                    key={platform.name}
                    href={platform.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#1a1a1a] hover:bg-gradient-to-r hover:from-orange-500 hover:to-red-600 text-white font-semibold px-8 py-4 rounded-full transition-all border border-gray-800 hover:border-transparent"
                  >
                    {platform.name}
                  </a>
                ))}
              </div>
            </div>
          </motion.section>
        </>
      )}
    </div>
  );
}
